/**
 * Torcaza Telemetry Client — Browser Edition
 * For Vue.js frontend apps. Uses Firebase ID Token for auth.
 */

const SERVICE_NAME = 'web-projects'

const getUrl = () => process.env.VUE_APP_TELEMETRY_URL || ''
const isEnabled = () => getUrl().length > 0

const RELEASE = process.env.VUE_APP_VERSION || 'unknown'
const ENVIRONMENT = process.env.NODE_ENV || 'production'

// ==========================================
// Auth — get Firebase ID token
// ==========================================

let getToken: (() => Promise<string | null>) | null = null

export function setTokenProvider(fn: () => Promise<string | null>) {
  getToken = fn
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (getToken) {
    const token = await getToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

// ==========================================
// Context — set after login
// ==========================================

let ctx: { tenantId: string; userId: string } | null = null

export function setTelemetryContext(tenantId: string, userId: string) {
  ctx = { tenantId, userId }
  logStatus()
}

export function clearTelemetryContext() {
  ctx = null
}

function getTenantId() { return ctx?.tenantId || 'unknown' }
function getUserId() { return ctx?.userId }

// ==========================================
// Breadcrumbs
// ==========================================

interface Breadcrumb { timestamp: string; type: string; category: string; message: string }

const MAX_BREADCRUMBS = 30
const breadcrumbs: Breadcrumb[] = []

export function addBreadcrumb(type: string, category: string, message: string) {
  breadcrumbs.push({ timestamp: new Date().toISOString(), type, category, message })
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift()
}

function getBreadcrumbs(): Breadcrumb[] { return [...breadcrumbs] }

// ==========================================
// Dedup
// ==========================================

const DEDUP_WINDOW_MS = 10_000
const recentErrors = new Map<string, { count: number; timer: ReturnType<typeof setTimeout> }>()

function shouldSendError(message: string): boolean {
  const key = message.replace(/\d+/g, '').slice(0, 80)
  if (recentErrors.has(key)) {
    recentErrors.get(key)!.count++
    return false
  }
  recentErrors.set(key, { count: 1, timer: setTimeout(() => recentErrors.delete(key), DEDUP_WINDOW_MS) })
  return true
}

// ==========================================
// HTTP transport
// ==========================================

let loggedStatus = false
function logStatus() {
  if (loggedStatus) return
  loggedStatus = true
  if (isEnabled()) console.log(`[Telemetry] Enabled → ${getUrl()}`)
  else console.log('[Telemetry] Disabled — VUE_APP_TELEMETRY_URL not set')
}

async function post(path: string, data: unknown): Promise<void> {
  if (!isEnabled()) return
  try {
    const headers = await getAuthHeaders()
    await fetch(`${getUrl()}${path}`, {
      method: 'POST', headers, body: JSON.stringify(data),
      signal: AbortSignal.timeout(5000),
    })
  } catch {
    // Fire-and-forget
  }
}

// ==========================================
// Batch buffer
// ==========================================

const buffer: { type: string; data: unknown }[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

function enqueue(type: string, data: unknown) {
  if (!isEnabled() || !ctx) return
  buffer.push({ type, data })
  if (buffer.length >= 30) {
    flush()
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, 10000)
  }
}

export function flush() {
  if (buffer.length === 0) return
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null }
  const batch = buffer.splice(0, buffer.length)
  const payload: Record<string, unknown[]> = {}
  for (const item of batch) {
    if (!payload[item.type]) payload[item.type] = []
    payload[item.type].push(item.data)
  }
  post('/ingest/batch', payload)
}

// ==========================================
// Auto Error Capture — browser edition
// ==========================================

let errorCaptureInitialized = false

export function initErrorCapture() {
  if (errorCaptureInitialized) return
  errorCaptureInitialized = true

  window.onerror = (message, source, lineno, colno, error) => {
    trackError({
      severity: 'error',
      category: 'uncaught-error',
      message: String(message),
      stackTrace: error?.stack,
      context: { source, lineno, colno },
    })
  }

  window.onunhandledrejection = (event) => {
    const reason = event.reason
    trackError({
      severity: 'error',
      category: 'unhandled-rejection',
      message: reason instanceof Error ? reason.message : String(reason),
      stackTrace: reason instanceof Error ? reason.stack : undefined,
    })
  }

  // Flush before page unload
  window.addEventListener('beforeunload', flush)

  console.log('[Telemetry] Browser error capture initialized')
}

// ==========================================
// Tracking functions
// ==========================================

export function trackError(event: {
  severity: 'critical' | 'error' | 'warning';
  category?: string; message: string; stackTrace?: string;
  context?: Record<string, unknown>;
}) {
  if (!shouldSendError(event.message)) return
  post('/ingest/error', {
    service: SERVICE_NAME,
    tenantId: getTenantId(),
    userId: getUserId(),
    ...event,
    release: RELEASE,
    environment: ENVIRONMENT,
    breadcrumbs: getBreadcrumbs(),
  })
}

export function trackRequest(event: {
  method?: string; path?: string; statusCode?: number; latencyMs?: number;
}) {
  addBreadcrumb('http', event.method || 'REQ', `${event.path || '/'} → ${event.statusCode || '?'}`)
  enqueue('requests', {
    service: SERVICE_NAME,
    tenantId: getTenantId(),
    userId: getUserId(),
    transport: 'http',
    ...event,
  })
}

export function trackFirestoreOp(event: {
  collection: string; operation: string; documentId?: string;
  reads?: number; writes?: number; deletes?: number; latencyMs?: number;
}) {
  enqueue('firestoreOps', {
    service: SERVICE_NAME,
    tenantId: getTenantId(),
    userId: getUserId(),
    ...event,
  })
}

export function trackTransfer(event: {
  direction: 'upload' | 'download';
  fileName?: string; fileType?: string; sizeBytes: number; latencyMs?: number;
}) {
  post('/ingest/transfer', {
    service: SERVICE_NAME,
    tenantId: getTenantId(),
    userId: getUserId(),
    ...event,
  })
}

export function trackActivity(event: {
  entityType: string; entityId: string; action: string;
  description?: string;
}) {
  addBreadcrumb('activity', event.action, `${event.entityType} ${event.entityId}`)
  enqueue('activities', {
    service: SERVICE_NAME,
    tenantId: getTenantId(),
    userId: getUserId(),
    ...event,
  })
}

export function trackPageView(path: string) {
  addBreadcrumb('navigation', 'route', path)
  enqueue('activities', {
    service: SERVICE_NAME,
    tenantId: getTenantId(),
    userId: getUserId(),
    entityType: 'page_view',
    entityId: path,
    action: 'viewed',
  })
}
